import { Component, OnInit, Input, Output } from '@angular/core';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { User } from '@angular/fire/auth';
import { DocumentReference } from '@angular/fire/firestore';
import { UserDocument } from '../../interfaces/user-document.interface';
import { ReferralService } from '../../services/referral.service';
import { AuthService } from '../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as QRCode from 'qrcode';

@Component({
  selector: 'app-invite-friends',
  templateUrl: './invite-friends.component.html',
  styleUrls: ['./invite-friends.component.css'],
  imports: [CommonModule, FormsModule, NgbModalModule],
})
export class InviteFriendsComponent implements OnInit {
  @Input() userData: UserDocument;
  @Input() userDocRef: DocumentReference | null;

  friendEmail: string = '';
  inviteLoading: boolean = false;
  emailError: string = '';
  qrCodeDataURL: string = '';
  qrCodeLoading: boolean = false;

  constructor(
    private modalService: NgbModal,
    private referralService: ReferralService,
    private authService: AuthService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void { }

  /**
   * Mở modal mời bạn bè
   */
  openInviteModal(content: any): void {
    this.modalService.open(content, { centered: true });
  }

  /**
   * Kiểm tra email hợp lệ
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  /**
   * Gửi lời mời đến bạn bè
   */
  async sendInvitation(): Promise<void> {
    this.emailError = '';

    if (!this.friendEmail) {
      this.emailError = 'Vui lòng nhập email';
      return;
    }

    if (!this.validateEmail(this.friendEmail)) {
      this.emailError = 'Email không hợp lệ';
      return;
    }

    this.inviteLoading = true;

    try {
      const currentUser = this.authService.userSubject.value;
      if (!currentUser || !currentUser.email) {
        throw new Error('Không tìm thấy thông tin người dùng');
      }
      if (!this.userDocRef) {
        throw new Error('Không tìm thấy thông tin tài khoản');
      }

      const success = await this.referralService.sendInvitation(
        currentUser.email,
        currentUser.uid,
        this.friendEmail
      );

      if (success) {
        this.friendEmail = '';
        this.modalService.dismissAll();
        this.toastr.success('Lời mời đã được gửi thành công!', 'Thành công');
      }
    } catch (error) {
      console.error('Error in sendInvitation:', error);
      this.toastr.error('Có lỗi xảy ra khi gửi lời mời', 'Lỗi');
    } finally {
      this.inviteLoading = false;
    }
  }

  /**
   * Tạo mã QR cho link giới thiệu
   */
  async generateQRCode(): Promise<void> {
    if (!this.userDocRef) {
      this.toastr.error('Không tìm thấy thông tin tài khoản', 'Lỗi');
      return;
    }

    this.qrCodeLoading = true;

    try {
      const registrationPath = 'dang-ky';
      const baseUrl = `https://longchau.id.vn/${registrationPath}?ref=${this.userDocRef.id}`;

      const qrCodeOptions = {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      };

      this.qrCodeDataURL = await QRCode.toDataURL(baseUrl, qrCodeOptions);
      this.toastr.success('Mã QR đã được tạo thành công!', 'Thành công');
    } catch (error) {
      console.error('Error generating QR code:', error);
      this.toastr.error('Có lỗi xảy ra khi tạo mã QR', 'Lỗi');
    } finally {
      this.qrCodeLoading = false;
    }
  }

  /**
   * Tải xuống mã QR
   */
  downloadQRCode(): void {
    if (!this.qrCodeDataURL) {
      this.toastr.warning('Vui lòng tạo mã QR trước', 'Thông báo');
      return;
    }

    const link = document.createElement('a');
    link.download = `referral-qr-${this.userDocRef?.id || 'code'}.png`;
    link.href = this.qrCodeDataURL;
    link.click();

    this.toastr.success('Mã QR đã được tải xuống!', 'Thành công');
  }

  /**
   * Chia sẻ mã giới thiệu (cập nhật)
   */
  shareReferralId(): void {
    if (!this.userDocRef) {
      this.toastr.error('Không tìm thấy thông tin tài khoản', 'Lỗi');
      return;
    }
    const registrationPath = 'dang-ky';
    const baseUrl = `https://longchau.id.vn/${registrationPath}?ref=${this.userDocRef.id}`;

    const shareText = `Tham gia LongLife với mã giới thiệu của tôi: ${this.userDocRef.id}. Đăng ký tại: ${baseUrl}`;

    if (navigator.share) {
      navigator.share({
        title: 'Mời bạn tham gia LongLife',
        text: shareText,
        url: baseUrl,
      }).catch((error) => console.error('Error sharing:', error));
    } else {
      navigator.clipboard
        .writeText(shareText)
        .then(() =>
          this.toastr.success('Đã sao chép liên kết giới thiệu', 'Thành công')
        )
        .catch((err) => this.toastr.error('Không thể sao chép liên kết', 'Lỗi'));
    }
  }

  /**
   * Chia sẻ mã QR qua Web Share API
   */
  async shareQRCode(): Promise<void> {
    if (!this.qrCodeDataURL) {
      this.toastr.warning('Vui lòng tạo mã QR trước', 'Thông báo');
      return;
    }

    try {
      // Chuyển đổi data URL thành blob
      const response = await fetch(this.qrCodeDataURL);
      const blob = await response.blob();
      const file = new File([blob], `referral-qr-${this.userDocRef?.id}.png`, { type: 'image/png' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Mã QR giới thiệu LongLife',
          text: 'Quét mã QR để tham gia LongLife với mã giới thiệu của tôi!',
          files: [file]
        });
      } else {
        // Fallback: copy image to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob
          })
        ]);
        this.toastr.success('Mã QR đã được sao chép vào clipboard!', 'Thành công');
      }
    } catch (error) {
      console.error('Error sharing QR code:', error);
      this.toastr.error('Không thể chia sẻ mã QR', 'Lỗi');
    }
  }
}
